<template>
  <Teleport to="body">
    <div
      v-if="isOpenRef"
      ref="containerRef"
      class="container__help"
      :style="styleContainerComputed"
    >
      <div
        ref="contentRef"
        class="content__help"
        :style="styleComputed"
        :class="{
          'content__help--top-left': currentItemComputed?.orientation === 'top-left',
          'content__help--top-center': currentItemComputed?.orientation === 'top-center',
          'content__help--top-right': currentItemComputed?.orientation === 'top-right',
          'content__help--bottom-left': currentItemComputed?.orientation === 'bottom-left',
          'content__help--bottom-center': currentItemComputed?.orientation === 'bottom-center',
          'content__help--bottom-right': currentItemComputed?.orientation === 'bottom-right',
          'content__help--left-top': currentItemComputed?.orientation === 'left-top',
          'content__help--left-center': currentItemComputed?.orientation === 'left-center',
          'content__help--left-bottom': currentItemComputed?.orientation === 'left-bottom',
          'content__help--right-top': currentItemComputed?.orientation === 'right-top',
          'content__help--right-center': currentItemComputed?.orientation === 'right-center',
          'content__help--right-bottom': currentItemComputed?.orientation === 'right-bottom',
        }"
      >
        <slot name="header">
          <div class="content__help--header">
            <span
              class="material-icons content__help--close"
              @click="close"
            >
              close
            </span>
          </div>
        </slot>
        <slot
          name="title"
          :title="currentItemComputed?.title"
        >
          <div
            v-if="currentItemComputed?.title"
            class="content__help--title"
          >
            <p>
              {{ currentItemComputed?.title }}
            </p>
          </div>
        </slot>
        <slot
          name="content"
          :content="currentItemComputed?.content"
          :current-index="indexRef"
        >
          <div class="content__help--texts">
            <div
              v-for="(theContent, index) in currentItemComputed?.content ?? []"
              :key="index"
            >
              <p v-if="typeof theContent === 'string' || typeof theContent === 'number'">
                {{ theContent }}
              </p>
            </div>
          </div>
        </slot>
        <slot
          name="buttons"
          :next="next"
          :back="back"
          :finish="onClickFinish"
          :current-index="indexRef"
          :dont-show-more-preview="onClickSkipPreview"
        >
          <div
            class="buttons__directions"
          >
            <span
              v-if="indexRef > 0"
              class="material-icons button__direction"
              @click="back"
            >
              arrow_back
            </span>
            <span v-else />
            <div class="paginator">
              <span>{{ paginatorText }}</span>
            </div>
            <span
              v-if="indexRef < props___.items.length - 1"
              class="material-icons button__direction"
              @click="next"
            >
              arrow_forward
            </span>
            <span
              v-if="indexRef === props___.items.length - 1"
              class="button__finish"
              @click="onClickFinish"
            >
              Finalizar
            </span>
          </div>
          <div
            v-if="indexRef < props___.items.length - 1 && props___.dontShowMoreMessage"
            class="buttons__skip-preview"
          >
            <a
              href="#"
              @click="onClickSkipPreview"
            >Saltar ayuda y no volver a mostrar</a>
          </div>
        </slot>
      </div>
    </div>
  </Teleport>
</template>


<script setup>
import {ref, computed, onUpdated, onMounted} from 'vue';

const CUSTOM_CLASS_LIGHT_DEFAULT = 'element--found--help';

const sizeArrow = 20;
const indexRef = ref(0);
const lastDirectionWasNextRef = ref(true);
const isOpenRef = ref(false);

const contentRef = ref();
const containerRef = ref();
const keyLocalStorage = computed(() => props___.customLocalStorageKey);

const props___ = defineProps({
  items: {
    type: Array,
    default: () => [],
  },
  dontShowMoreMessage: {
    type: Boolean,
    default: false,
  },
  customLocalStorageKey: {
    type: String,
    default: 'dontShowHelpDefault',
  },
});

const styleComputed = ref(`top: 0px; left: 0px`);
const styleContainerComputed = ref(``);
const currentItemComputed = computed(() => props___.items[indexRef.value]);

const paginatorText = computed(() => {
  const index1 = indexRef.value + 1;
  const length1 = props___.items.length;
  return (index1 + ' de ' + length1);
});
// #region Props, Emits
const emits = defineEmits(['finish', 'next', 'back', 'close', 'open', 'beforeUpdate']);
// #endregion


// #region Params, Ref, Reactives, Computed
// ...
// #endregion


// #region Functions
/**
 * Return a object with the position of content or null
 *
 * @return {Object | null}
 * @returnExample {
 *  top: String,
 *  left: String,
 *  right: String
 * }
 */
function findPositionElement() {
  const {orientation, callbackFindPosition} = currentItemComputed?.value ?? {};

  // Si el item ya tiene su función de autoposicionarse se retorna su ejecución
  if (callbackFindPosition) {
    return callbackFindPosition();
  }

  const widthContainer = containerRef.value?.getBoundingClientRect()?.width ?? 0;
  const heightContainer = containerRef.value?.getBoundingClientRect()?.height ?? 0;

  const widthContent = contentRef.value?.getBoundingClientRect().width ?? 0;
  const heightContent = contentRef.value?.getBoundingClientRect().height ?? 0;

  const elementFound = findComponent();
  let elementBoundingClientRect = elementFound?.getBoundingClientRect() ?? null;
  if (elementBoundingClientRect) {
    const {height, width} = elementBoundingClientRect;
    if (!height || !width) {
      elementBoundingClientRect = null;
    }
  }

  if (elementFound && elementBoundingClientRect) {
    const {top, height, left, width} = elementBoundingClientRect;

    switch (orientation.toLocaleLowerCase()) {
      case 'top-left':
        return {
          top: `${top + height + sizeArrow}px`,
          left: `${left}px`,
        };
      case 'top-center':
        return {
          top: `${top + height + sizeArrow}px`,
          left: `${left + (width/2 - widthContent/2)}px`,
        };
      case 'top-right':
        return {
          top: `${top + height + sizeArrow}px`,
          right: `${widthContainer - (left + width)}px`,
        };
      case 'bottom-left':
        return {
          top: `${top - heightContent - sizeArrow}px`,
          left: `${left}px`,
        };
      case 'bottom-center':
        return {
          top: `${top - heightContent - sizeArrow}px`,
          left: `${left + (width/2 - widthContent/2)}px`,
        };
      case 'bottom-right':
        return {
          top: `${top - heightContent - sizeArrow}px`,
          right: `${widthContainer - (left + width)}px`,
        };
      case 'left-top':
        return {
          top: `${top}px`,
          left: `${left + width + sizeArrow}px`,
        };
      case 'left-center':
        return {
          top: `${top + (height/2 - heightContent/2)}px`,
          left: `${left + width + sizeArrow}px`,
        };
      case 'left-bottom':
        return {
          top: `${top + height - heightContent}px`,
          left: `${left + width + sizeArrow}px`,
        };
      case 'right-top':
        return {
          top: `${top}px`,
          left: `${left - widthContent - sizeArrow}px`,
        };
      case 'right-center':
        return {
          top: `${top + (height/2 - heightContent/2)}px`,
          left: `${left - widthContent - sizeArrow}px`,
        };
      case 'right-bottom':
        return {
          top: `${top + height - heightContent}px`,
          left: `${left - widthContent - sizeArrow}px`,
        };
      default:
        return {
          left: currentItemComputed?.value?.left ?? `${(widthContainer/2 - widthContent/2)}px`,
          top: currentItemComputed?.value?.top ?? `${(heightContainer/2 - heightContent/2)}px`,
        };
    }
  }
  if (currentItemComputed?.value?.skipWithoutElement) {
    return null;
  }
  return {
    left: currentItemComputed?.value?.left ?? `${(widthContainer/2 - widthContent/2)}px`,
    top: currentItemComputed?.value?.top ?? `${(heightContainer/2 - heightContent/2)}px`,
  };
}

/**
 * Find the currentComponent
 *
 * @return {ObjectHTML}
 */
function findComponent() {
  const {element, querySelector, showLight, customClassLight} = currentItemComputed?.value ?? {};
  const customClassLightDefault = customClassLight ?? CUSTOM_CLASS_LIGHT_DEFAULT;

  let elementFound = element;
  if (!element && querySelector) {
    elementFound = querySelector();
  }

  if (elementFound && showLight && !elementFound.classList.contains(customClassLightDefault)) {
    elementFound.classList.add(customClassLightDefault);
  }

  return elementFound;
}

function removeActionLight() {
  const {customClassLight, showLight} = currentItemComputed?.value ?? {};
  const customClassLightDefault = customClassLight ?? CUSTOM_CLASS_LIGHT_DEFAULT;

  const currentElement = findComponent();
  if (currentElement && showLight) {
    currentElement.classList.remove(customClassLightDefault);
  }
}

/**
 * Update the indexRef with the next value
 *
 * @emits 'next' {currentIndex}
 *
 */
function next() {
  removeActionLight();

  if (currentItemComputed?.value && currentItemComputed?.value?.preCallbackNext) {
    currentItemComputed.value.preCallbackNext();
  }

  let newValue = indexRef.value + 1;

  if (newValue > (props___.items.length - 1)) {
    newValue = props___.items.length - 1;
  }
  emits('beforeUpdate', newValue);
  indexRef.value = newValue;
  lastDirectionWasNextRef.value = true;

  emits('next', indexRef.value);

  if (props___.items[indexRef.value - 1] && props___.items[indexRef.value - 1].postCallbackNext) {
    props___.items[indexRef.value - 1].postCallbackNext();
  }
}

/**
 * Update the indexRef with the previous value
 *
 * @emits 'back' {currentIndex}
 *
 */
function back() {
  removeActionLight();

  if (currentItemComputed?.value && currentItemComputed?.value?.preCallbackBack) {
    currentItemComputed.value.preCallbackBack();
  }

  let newValue = indexRef.value - 1;

  if (newValue < 0) {
    newValue = 0;
  }
  emits('beforeUpdate', newValue);
  indexRef.value = newValue;
  lastDirectionWasNextRef.value = false;

  emits('back', indexRef.value);

  if (props___.items[indexRef.value + 1] && props___.items[indexRef.value + 1].postCallbackBack) {
    props___.items[indexRef.value + 1].postCallbackBack();
  }
}

/**
 * Update the indexRef with the previous value
 *
 */
function onClickFinish() {
  removeActionLight();
  emits('finish');
}

/**
 * Open the component
 *
 */
function open() {
  isOpenRef.value = true;
  emits('beforeUpdate', 0);
  indexRef.value = 0;
  emits('open');

  window.addEventListener('resize', updatePosition);
}

/**
 * Close the component
 *
 */
function close() {
  removeActionLight();
  isOpenRef.value = false;
  emits('close');

  window.removeEventListener('resize', updatePosition);
}

/**
 * Update the position of the value of ref
 *
 */
function getCurrentPosition() {
  const newPosition = findPositionElement();
  if (!!newPosition) {
    let {top, left, right} = newPosition;

    if (!left) {
      left = 'auto';
    }
    if (!right) {
      right = 'auto';
    }
    return `top: ${top}; left: ${left}; right: ${right}`;
  }
  return null;
}

/**
 * Update the position of the value of ref
 *
 */
function getCustomStyles() {
  const {customStyles} = currentItemComputed?.value ?? {};
  let styles = '';
  if (customStyles) {
    for (const name of Object.keys(customStyles)) {
      const prop = name.split(/(?=[A-Z])/).join('-').toLowerCase();
      const value = customStyles[name];
      styles += `${prop}: ${value};`;
    }
  }
  return styles;
}

/**
 * Save the value in localStorage
 *
 * @param {Any} value
 *
 */
function setLocalStorage(value) {
  window.localStorage.setItem(keyLocalStorage.value, value);
}

/**
 * Return the value of localStorage
 *
 * @return {Any}
 */
function getLocalStorage() {
  return window.localStorage.getItem(keyLocalStorage.value);
}

/**
 * Event when the skip preview has been clicked
 *
 * @param {Event} $event
 *
 */
function onClickSkipPreview($event) {
  $event.preventDefault();
  setLocalStorage(true);
  close();
}

/**
 * Update the computed of style
 *
 */
function updatePosition() {
  const newPosition = getCurrentPosition();
  if (!!newPosition) {
    styleComputed.value = getCustomStyles() + getCurrentPosition();
    return;
  }
  if (lastDirectionWasNextRef.value) {
    next();
    return;
  }
  back();
  return;
}

/**
 * Check if the component must will be opened
 *
 * @return {Boolean}
 */
function mustOpenDefault() {
  const valueLocalStorage = getLocalStorage();
  return !valueLocalStorage;
}

// #endregion


// #region Effects, Hooks

onUpdated(() => {
  if (isOpenRef.value) {
    updatePosition();
  }
});

onMounted(() => {
  if (isOpenRef.value) {
    updatePosition();
  }
});
// #endregion


// #region Provide, Inject, Expose

defineExpose({
  next,
  back,
  open,
  close,
  mustOpenDefault,
  keyLocalStorage: props___.customLocalStorageKey,
});
// #endregion
</script>


<style lang="scss" scoped>
.container__help {
  position: absolute;
  height: 100%;
  width: 100%;
  top: 0;
  left: 0;
  background-color: rgba($brand__color--black, 0.1);
  z-index: 5000;
}

.content__help {
  position: absolute;
  background-color: rgba($brand__color--blue, 1);
  padding: 5px 10px 10px 10px;
  left: 0;
  top: 0;
  color: white;
  height: fit-content;
  max-width: calc(60%);
  min-width: 200px;
  box-shadow: 0 25.6px 57.6px rgb(0 0 0 / 22%),
              0 4.8px 14.4px rgb(0 0 0 / 18%);

  &--header {
    display: flex;
    justify-content: end;
  }

  &--close {
    cursor: pointer;
  }

  &--texts {
    padding: 5px 0px;

    & p {
      margin: 0;
      margin-bottom: 10px;
    }

  }

  &--title {
    width: 100%;
    font-weight: bold;
    font-size: 1.3rem;
    border-radius: 0.25rem;
    margin-bottom: 5px;

    & > p {
      font-size: inherit;
      margin: 0;
    }
  }

  // =========== ORIENTATION ===========

  // =========== TOP ===========
  &--top-left {
    border-top-left-radius: 0;
    box-shadow: 2px 2px 4px 0px rgba($brand__color--black, 0.5),
    2px 2px 4px 0px rgba($brand__color--black, 0.5);

    &:after {
      content: '';
      top: -20px;
      left: 0px;
      position: absolute;
      border-top: 20px solid transparent;
      border-left: 20px solid rgba($brand__color--blue, 1);
    }
  }

  &--top-center {
    box-shadow: 2px 2px 4px 0px rgba($brand__color--black, 0.5),
    -2px 2px 4px 0px rgba($brand__color--black, 0.5);
    &:after {
      content: '';
      top: -20px;
      left: calc(50% - 10px);
      margin: 0 auto;
      position: absolute;
      border-left: 10px solid transparent;
      border-right: 10px solid transparent;
      border-bottom: 20px solid rgba($brand__color--blue, 1);
    }
  }

  &--top-right {
    box-shadow: -2px 2px 4px 0px rgba($brand__color--black, 0.5),
    -2px 2px 4px 0px rgba($brand__color--black, 0.5);
    border-top-right-radius: 0;
    &:after {
      content: '';
      top: -20px;
      right: 0px;
      position: absolute;
      border-top: 20px solid transparent;
      border-right: 20px solid rgba($brand__color--blue, 1);
    }
  }


  // =========== BOTTOM ===========
  &--bottom-left {
    box-shadow: 2px -2px 4px 0px rgba($brand__color--black, 0.5),
    2px -2px 4px 0px rgba($brand__color--black, 0.5);
    border-bottom-left-radius: 0;
    &:after {
      content: '';
      bottom: -20px;
      left: 0px;
      position: absolute;
      border-bottom: 20px solid transparent;
      border-left: 20px solid rgba($brand__color--blue, 1);
    }
  }

  &--bottom-center {
    box-shadow: 2px -2px 4px 0px rgba($brand__color--black, 0.5),
    -2px -2px 4px 0px rgba($brand__color--black, 0.5);
    &:after {
      content: '';
      bottom: -20px;
      left: calc(50% - 10px);
      margin: 0 auto;
      position: absolute;
      border-left: 10px solid transparent;
      border-right: 10px solid transparent;
      border-top: 20px solid rgba($brand__color--blue, 1);
    }
  }

  &--bottom-right {
    box-shadow: -2px -2px 4px 0px rgba($brand__color--black, 0.5),
    -2px -2px 4px 0px rgba($brand__color--black, 0.5);
    border-bottom-right-radius: 0;
    &:after {
      content: '';
      bottom: -20px;
      right: 0px;
      position: absolute;
      border-bottom: 20px solid transparent;
      border-right: 20px solid rgba($brand__color--blue, 1);
    }
  }


  // =========== LEFT ===========
  &--left-top {
    box-shadow: 2px 2px 4px 0px rgba($brand__color--black, 0.5),
    2px 2px 4px 0px rgba($brand__color--black, 0.5);
    border-top-left-radius: 0;
    &:after {
      content: '';
      top: 0;
      left: -20px;
      position: absolute;
      border-left: 20px solid transparent;
      border-top: 20px solid rgba($brand__color--blue, 1);
    }
  }

  &--left-center {
    box-shadow: 2px -2px 4px 0px rgba($brand__color--black, 0.5),
    2px 2px 4px 0px rgba($brand__color--black, 0.5);
    &:after {
      content: '';
      top: calc(50% - 10px);
      left: -20px;
      margin: 0 auto;
      position: absolute;
      border-top: 10px solid transparent;
      border-bottom: 10px solid transparent;
      border-right: 20px solid rgba($brand__color--blue, 1);
    }
  }

  &--left-bottom {
    box-shadow: 2px -2px 4px 0px rgba($brand__color--black, 0.5),
    2px -2px 4px 0px rgba($brand__color--black, 0.5);
    border-bottom-left-radius: 0;
    &:after {
      content: '';
      bottom: 0;
      left: -20px;
      position: absolute;
      border-left: 20px solid transparent;
      border-bottom: 20px solid rgba($brand__color--blue, 1);
    }
  }


  // =========== RIGHT ===========
  &--right-top {
    box-shadow: -2px 2px 4px 0px rgba($brand__color--black, 0.5),
    -2px 2px 4px 0px rgba($brand__color--black, 0.5);
    border-top-right-radius: 0;
    &:after {
      content: '';
      top: 0;
      right: -20px;
      position: absolute;
      border-right: 20px solid transparent;
      border-top: 20px solid rgba($brand__color--blue, 1);
    }
  }

  &--right-center {
    box-shadow: -2px -2px 4px 0px rgba($brand__color--black, 0.5),
    -2px 2px 4px 0px rgba($brand__color--black, 0.5);
    &:after {
      content: '';
      top: calc(50% - 10px);
      right: -20px;
      margin: 0 auto;
      position: absolute;
      border-top: 10px solid transparent;
      border-bottom: 10px solid transparent;
      border-left: 20px solid rgba($brand__color--blue, 1);
    }
  }

  &--right-bottom {
    box-shadow: -2px -2px 4px 0px rgba($brand__color--black, 0.5),
    -2px -2px 4px 0px rgba($brand__color--black, 0.5);
    border-bottom-right-radius: 0;
    &:after {
      content: '';
      bottom: 0;
      right: -20px;
      position: absolute;
      border-right: 20px solid transparent;
      border-bottom: 20px solid rgba($brand__color--blue, 1);
    }
  }


}

.buttons__directions {
  display: flex;
  justify-content: space-between;
  margin-top: 10px;
  align-items: center;

  & > .button__direction {
    padding: 5px;
    border: 1px solid white;
    border-radius: 50%;
    cursor: pointer;
    box-shadow: 2px 2px 4px 0px rgba($brand__color--black, 0.3),
    2px 2px 4px 0px rgba($brand__color--black, 0.3);
    transition: all linear 0.25s;

    &:hover {
      box-shadow: none;
    }
  }

  & > .button__finish {
    padding: 5px;
    border: 1px solid white;
    border-radius: 0.25rem;
    cursor: pointer;
    box-shadow: 2px 2px 4px 0px rgba($brand__color--black, 0.3),
    2px 2px 4px 0px rgba($brand__color--black, 0.3);
    transition: all linear 0.25s;
    height: fit-content;

    &:hover {
      box-shadow: none;
    }
  }

  & > .dots {
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 0 5px;

    & > .dot {
      margin: 2px;
      width: 5px;
      height: 5px;
      background-color: $brand__color--white;
      border-radius: 50%;

      &--selected {
        width: 7px;
        height: 7px;
      }
    }
  }
}

.buttons__skip-preview {
  display: flex;
  justify-content: center;

  & a {
    color: white;
    border-bottom: 1px solid transparent;

    &:hover {
      border-bottom: 1px solid white;
    }
  }
}

</style>

<style lang="scss">
.element--found--help {
  animation: light 3s infinite;
}

@keyframes light {
  0% { box-shadow: 0px 0px 1px 1px #fff; }
  50% { box-shadow: 0px 0px 10px 10px #fff; }
  100% { box-shadow: 0px 0px 1px 1px #fff; }
}
</style>
